import { ScrollText } from "lucide-react";
import type { RequestEvent } from "@/hooks/use-staffing-requests";

const EVENT_LABELS: Record<string, string> = {
  ingested: "Ingested",
  linked_solution: "Solution Linked",
  line_mapped: "Line Mapped",
  line_resolved: "Line Resolved",
  intensity_confirmed: "Intensity Confirmed",
  pod_staged: "Pod Staged",
  committed: "Committed",
  partially_committed: "Partially Committed",
  rejected: "Rejected",
  parse_error: "Parse Error",
  title_renamed: "Renamed",
};

interface Props {
  events: RequestEvent[];
}

/** Render a jsonb decision value (object | scalar | null) as compact, readable text. */
function formatValue(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>).filter(
      ([, v]) => v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0),
    );
    if (entries.length === 0) return null;
    return entries
      .map(([k, v]) => {
        if (Array.isArray(v)) return `${k}: ${v.length}`;
        if (typeof v === "object") return `${k}: ${JSON.stringify(v)}`;
        return `${k}: ${String(v)}`;
      })
      .join(", ");
  }
  return String(value);
}

export function DecisionLog({ events }: Props) {
  if (events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <ScrollText className="mb-2 h-6 w-6 text-muted-foreground/50" />
        <p className="text-[12px] text-muted-foreground">No events recorded yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {events.map((evt) => (
        <EventRow key={evt.id} event={evt} />
      ))}
    </div>
  );
}

function EventRow({ event }: { event: RequestEvent }) {
  const when = new Date(event.eventAt).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  const label = EVENT_LABELS[event.eventType] ?? event.eventType;
  const systemText = formatValue(event.suggestedValue);
  const humanText = formatValue(event.humanValue);

  return (
    <div className="flex gap-3 border-t border-border py-2.5 first:border-t-0">
      <div className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-foreground/25 mt-1.5" />

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-[12px] font-medium text-foreground">{label}</span>
          <span className="text-[10px] text-muted-foreground">{when}</span>
          {event.lineId && (
            <span className="rounded bg-muted px-1 py-0.5 text-[9px] text-muted-foreground">
              line
            </span>
          )}
        </div>

        {(systemText !== null || humanText !== null) && (
          <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">
            {systemText !== null && (
              <span className="flex items-center gap-1">
                <span className="text-muted-foreground">System:</span>
                <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px]">
                  {systemText}
                </span>
              </span>
            )}
            {humanText !== null && (
              <span className="flex items-center gap-1">
                <span className="text-muted-foreground">RM:</span>
                <span className="rounded bg-foreground/8 px-1.5 py-0.5 font-mono text-[10px]">
                  {humanText}
                </span>
              </span>
            )}
          </div>
        )}

        {event.reason && <p className="mt-0.5 text-[11px] text-muted-foreground">{event.reason}</p>}
      </div>
    </div>
  );
}
